import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { i18n } from 'next-i18next';
import { useLocaleContext } from '@storefront/lib-global';
import {
  type GroupedByCountry,
  Locale,
  LocaleStar2,
  countryNameMapper,
  countryNameMapperROE,
  getCountryKey,
  groupByCountry,
} from '@storefront/lib-global/utils/internationalization';
import DELIVERY_COUNTRIES_DE_AT from '../data/de-at/delivery-countries.json';
import DELIVERY_COUNTRIES_DE_CH from '../data/de-ch/delivery-countries.json';
import DELIVERY_COUNTRIES_DE_DE from '../data/de-de/delivery-countries.json';
import BILLING_COUNTRIES_DE from '../data/de/billing-countries.json';
import REGIONS_EN_CA from '../data/fr-ca/regions.json';
import BILLING_COUNTRIES_EN_GB from '../data/en-gb/billing-countries.json';
import DELIVERY_COUNTRIES_EN_GB from '../data/en-gb/delivery-countries.json';
import REGIONS_EN_US from '../data/en-us/regions.json';
import BILLING_COUNTRIES_EN_VG from '../data/en-vg/billing-countries.json';
import DELIVERY_COUNTRIES_EN_VG from '../data/en-vg/delivery-countries.json';
import BILLING_COUNTRIES_ES_ES from '../data/es-es/billing-countries.json';
import DELIVERY_COUNTRIES_ES_ES from '../data/es-es/delivery-countries.json';
import DELIVERY_COUNTRIES_FR_BE from '../data/fr-be/delivery-countries.json';
import REGIONS_FR_CA from '../data/fr-ca/regions.json';
import DELIVERY_COUNTRIES_FR_CH from '../data/fr-ch/delivery-countries.json';
import DELIVERY_COUNTRIES_FR_FR from '../data/fr-fr/delivery-countries.json';
import DELIVERY_COUNTRIES_FR_LU from '../data/fr-lu/delivery-countries.json';
import BILLING_COUNTRIES_FR from '../data/fr/billing-countries.json';
import DELIVERY_COUNTRIES_IT_CH from '../data/it-ch/delivery-countries.json';
import DELIVERY_COUNTRIES_IT_IT from '../data/it-it/delivery-countries.json';
import BILLING_COUNTRIES_IT from '../data/it/billing-countries.json';
import DELIVERY_COUNTRIES_NL_BE from '../data/nl-be/delivery-countries.json';
import DELIVERY_COUNTRIES_NL_NL from '../data/nl-nl/delivery-countries.json';
import BILLING_COUNTRIES_NL from '../data/nl/billing-countries.json';
import type { Countries, Regions } from '../types';

type CheckoutStaticContextType = {
  deliveryCountries: Countries | null | undefined;
  billingCountries: Countries | null | undefined;
  regions: Regions | null | undefined;
  countries: Countries | null | undefined;
  regionsUS: Regions | null | undefined;
  regionsCA: Regions | null | undefined;
  countryInfo: GroupedByCountry;
};

const CheckoutStaticContext = createContext<CheckoutStaticContextType>({
  deliveryCountries: {},
  billingCountries: {},
  regions: {},
  countries: {},
  regionsUS: {},
  regionsCA: {},
  countryInfo: {},
});

export const useCheckoutStaticContext = () => useContext(CheckoutStaticContext);

type CheckoutStaticContextProviderProps = {
  children: ReactNode;
};

// CheckoutStaticContext should contain non-changing or rarely-changing variables
// used in any part of Checkout.
export function CheckoutStaticContextProvider({ children }: CheckoutStaticContextProviderProps) {
  const { locale } = useRouter();
  const { locale: localeContext } = useLocaleContext();

  const regionsCA: Regions = locale === Locale.FR_CA ? REGIONS_FR_CA : REGIONS_EN_CA;
  const regionsUS: Regions = REGIONS_EN_US;
  const countries: Countries = BILLING_COUNTRIES_EN_GB;
  const lang = locale?.split('-')[0]?.toLowerCase();
  let countriesData;

  if (
    // it-ch
    locale &&
    i18n?.services.resourceStore.data[locale] &&
    Object.keys(i18n?.services.resourceStore.data[locale]['lib-global-countries-common'] || {}).length > 0
  ) {
    countriesData = i18n?.services.resourceStore.data[locale]['lib-global-countries-common'];
  } else if (
    // it
    lang &&
    i18n?.services.resourceStore.data[lang] &&
    Object.keys(i18n?.services.resourceStore.data[lang]['lib-global-countries-common'] || {}).length > 0
  ) {
    countriesData = i18n?.services.resourceStore.data[lang]['lib-global-countries-common'];
  } else {
    // default en
    countriesData = i18n?.services.resourceStore.data[localeContext]['lib-global-countries-common'];
  }
  const countryInfo = groupByCountry(countriesData as Record<string, string>);

  const deliveryCountries: Countries = useMemo(() => {
    switch (locale) {
      case Locale.STOREFRONT_OUTLET_LOCALE:
        return DELIVERY_COUNTRIES_EN_VG;
      case LocaleStar2.EN_AT:
      case LocaleStar2.EN_BE:
      case LocaleStar2.EN_BG:
      case LocaleStar2.EN_CH:
      case LocaleStar2.EN_CY:
      case LocaleStar2.EN_CZ:
      case LocaleStar2.EN_DK:
      case LocaleStar2.EN_FI:
      case LocaleStar2.EN_GR:
      case LocaleStar2.EN_HR:
      case LocaleStar2.EN_HU:
      case LocaleStar2.EN_LI:
      case LocaleStar2.EN_LU:
      case LocaleStar2.EN_LV:
      case LocaleStar2.EN_MT:
      case LocaleStar2.EN_PL:
      case LocaleStar2.EN_PT: {
        const countryKey = getCountryKey(locale).toUpperCase();
        return {
          [countryKey]: countryNameMapperROE[countryKey],
        };
      }
      case Locale.EN_CA:
      case Locale.EN_IE:
      case Locale.EN_US:
      case Locale.FR_CA:
      case LocaleStar2.EN_DE:
      case LocaleStar2.EN_ES:
      case LocaleStar2.EN_FR:
      case LocaleStar2.EN_IT:
      case LocaleStar2.EN_NL: {
        const countryKey = getCountryKey(locale).toUpperCase();
        return {
          [countryKey]: countryNameMapper[countryKey],
        };
      }
      case LocaleStar2.ES_ES:
        return DELIVERY_COUNTRIES_ES_ES;
      case LocaleStar2.FR_BE:
        return DELIVERY_COUNTRIES_FR_BE;
      case LocaleStar2.FR_CH:
        return DELIVERY_COUNTRIES_FR_CH;
      case LocaleStar2.FR_FR:
        return DELIVERY_COUNTRIES_FR_FR;
      case LocaleStar2.FR_LU:
        return DELIVERY_COUNTRIES_FR_LU;
      case LocaleStar2.IT_CH:
        return DELIVERY_COUNTRIES_IT_CH;
      case LocaleStar2.IT_IT:
        return DELIVERY_COUNTRIES_IT_IT;
      case LocaleStar2.NL_BE:
        return DELIVERY_COUNTRIES_NL_BE;
      case LocaleStar2.NL_NL:
        return DELIVERY_COUNTRIES_NL_NL;
      case LocaleStar2.DE_AT:
        return DELIVERY_COUNTRIES_DE_AT;
      case LocaleStar2.DE_CH:
        return DELIVERY_COUNTRIES_DE_CH;
      case LocaleStar2.DE_DE:
        return DELIVERY_COUNTRIES_DE_DE;
      case Locale.EN_GB:
      default:
        return DELIVERY_COUNTRIES_EN_GB;
    }
  }, [locale]);

  const billingCountries: Countries = useMemo(() => {
    switch (locale) {
      case Locale.EN_CA:
      case Locale.EN_US:
      case Locale.FR_CA: {
        const countryKey = getCountryKey(locale).toUpperCase();
        return {
          [countryKey]: countryNameMapper[countryKey],
        };
      }
      case Locale.STOREFRONT_OUTLET_LOCALE:
        return BILLING_COUNTRIES_EN_VG;
      case LocaleStar2.DE_AT:
      case LocaleStar2.DE_CH:
      case LocaleStar2.DE_DE:
        return BILLING_COUNTRIES_DE;
      case LocaleStar2.ES_ES:
        return BILLING_COUNTRIES_ES_ES;
      case LocaleStar2.FR_BE:
      case LocaleStar2.FR_CH:
      case LocaleStar2.FR_FR:
      case LocaleStar2.FR_LU:
        return BILLING_COUNTRIES_FR;
      case LocaleStar2.NL_NL:
      case LocaleStar2.NL_BE:
        return BILLING_COUNTRIES_NL;
      case LocaleStar2.IT_CH:
      case LocaleStar2.IT_IT:
        return BILLING_COUNTRIES_IT;
      case Locale.EN_GB:
      case Locale.EN_IE:
      case LocaleStar2.EN_AT:
      case LocaleStar2.EN_BE:
      case LocaleStar2.EN_BG:
      case LocaleStar2.EN_CH:
      case LocaleStar2.EN_CY:
      case LocaleStar2.EN_CZ:
      case LocaleStar2.EN_DK:
      case LocaleStar2.EN_FI:
      case LocaleStar2.EN_GR:
      case LocaleStar2.EN_HR:
      case LocaleStar2.EN_HU:
      case LocaleStar2.EN_LI:
      case LocaleStar2.EN_LU:
      case LocaleStar2.EN_LV:
      case LocaleStar2.EN_MT:
      case LocaleStar2.EN_PL:
      case LocaleStar2.EN_PT:
      case LocaleStar2.EN_DE:
      case LocaleStar2.EN_ES:
      case LocaleStar2.EN_FR:
      case LocaleStar2.EN_IT:
      case LocaleStar2.EN_NL:
      default:
        return BILLING_COUNTRIES_EN_GB;
    }
  }, [locale]);

  const staticContext = useMemo(() => {
    const regions = { ...regionsCA, ...regionsUS };
    return {
      deliveryCountries,
      billingCountries,
      regions,
      countries,
      regionsUS,
      regionsCA,
      countryInfo,
    };
  }, [regionsCA, regionsUS, deliveryCountries, billingCountries, countries, countryInfo]);

  return <CheckoutStaticContext.Provider value={staticContext}>{children}</CheckoutStaticContext.Provider>;
}
